import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Chip, CircularProgress, IconButton, MenuItem, TextField, Tooltip, Typography } from '@mui/material';
import { Add, Block, Draw, Sync, TaskAlt, UploadFile, Visibility } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import ConfirmModal from './modals/ConfirmModal';
import IssueContractModal from './modals/IssueContractModal';
import SendForSignatureModal from './modals/SendForSignatureModal';
import { contractSignatureService, ContractSignatureStatus, SIGNATURE_STATUS_LABELS, signatureErrorMessage } from '../services/signatureService';
import ClearFiltersButton from './ClearFiltersButton';
import StandardPagination, { ITENS_POR_PAGINA_PADRAO } from './Pagination/StandardPagination';
import { Toast } from './Toast';
import { useToast } from '../hooks/useToast';
import { colors } from '../theme/designSystem';
import { filterActionButtonSx, dateFieldSx, filterFieldSx, searchFieldSx } from '../theme/filterStyles';
import {
  contractIssueErrorMessage,
  contractsService,
  ContractRow,
  ContractStatus,
  ContractSubject,
  CONTRACT_PERMISSIONS,
  CONTRACT_STATUS_LABELS,
  IssuableContractTemplate,
  PROFESSIONAL_CONTRACT_PERMISSIONS,
} from '../services/contractsService';

/**
 * Contratos de um contratante — a aba Contratos do cadastro do paciente (item 66, fase 2; RN-539) e a
 * página Contratos do usuário profissional (item 236): mesmo esqueleto das demais abas — filtros
 * reativos, limpar, emitir na barra, lista com acordeão para ler o texto. Ver é a permissão da
 * aba/página; emitir e cancelar aparecem só para quem tem as permissões-filhas do público. Contrato
 * não se exclui: cancela com motivo e continua na lista, marcado.
 */

const formatDate = (iso: string | null) => (iso ? iso.slice(0, 10).split('-').reverse().join('/') : '');
const formatDateTime = (iso: string | null) => (iso ? new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '');
const stripHtml = (html: string) => html.replace(/<[^>]+>/g, ' ').replace(/&nbsp;/g, ' ');

const STATUS_STYLE: Record<ContractStatus, { bg: string; fg: string }> = {
  issued: { bg: colors.primarySoft, fg: colors.primaryText },
  signed: { bg: colors.successSoft, fg: colors.successHover },
  cancelled: { bg: colors.errorSoft, fg: colors.errorHover },
};

/** Vigência lida na data de hoje do navegador: só orienta a leitura; a regra (RN-558e) fica no servidor. */
const validityLabel = (c: ContractRow): { text: string; color: string } => {
  const today = new Date().toISOString().slice(0, 10);
  if (c.status === 'cancelled') return { text: `Cancelado em ${formatDate(c.cancelledAt)}`, color: colors.errorHover };
  if (c.validFrom > today) return { text: `Vigência a partir de ${formatDate(c.validFrom)}`, color: colors.textSecondary };
  if (c.validTo && c.validTo < today) {
    return c.autoRenew
      ? { text: `Renovado automaticamente (1ª vigência até ${formatDate(c.validTo)})`, color: colors.successHover }
      : { text: `Vencido em ${formatDate(c.validTo)}`, color: colors.warningSoftText };
  }
  return { text: c.validTo ? `Vigente até ${formatDate(c.validTo)}` : 'Vigente, prazo indeterminado', color: colors.successHover };
};

interface PanelProps {
  subject: ContractSubject;
}

export const ContractsPanel: React.FC<PanelProps> = ({ subject }) => {
  const { user } = useAuth();
  const { toast, showToast, hideToast } = useToast();
  const allowed = user?.allowedPages || [];
  const permissions = subject.kind === 'professional' ? PROFESSIONAL_CONTRACT_PERMISSIONS : CONTRACT_PERMISSIONS;
  const canIssue = allowed.includes(permissions.issue);
  const canCancel = allowed.includes(permissions.cancel);
  const subjectId = subject.id;
  const subjectKind = subject.kind;
  const subjectNoun = subjectKind === 'professional' ? 'este profissional' : 'este paciente';

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(ITENS_POR_PAGINA_PADRAO);
  const [items, setItems] = useState<ContractRow[]>([]);
  const [templates, setTemplates] = useState<IssuableContractTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [issueOpen, setIssueOpen] = useState(false);
  const [cancelTarget, setCancelTarget] = useState<ContractRow | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [signTarget, setSignTarget] = useState<ContractRow | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const attachInput = React.useRef<HTMLInputElement | null>(null);
  const [attachTarget, setAttachTarget] = useState<ContractRow | null>(null);

  const load = useCallback(async () => {
    if (!subjectId) { setItems([]); setLoading(false); return; }
    setLoading(true);
    try {
      const [rows, issuable] = await Promise.all([
        contractsService.getBySubject({ kind: subjectKind, id: subjectId }),
        canIssue ? contractsService.getIssuableTemplates(subjectKind).catch(() => [] as IssuableContractTemplate[]) : Promise.resolve([] as IssuableContractTemplate[]),
      ]);
      setItems(rows);
      setTemplates(issuable);
    } catch {
      showToast('Não foi possível carregar os contratos.', 'error');
    } finally {
      setLoading(false);
    }
  }, [subjectId, subjectKind, canIssue, showToast]);

  useEffect(() => { load(); }, [load]);

  // Ao abrir a aba, pergunta ao provedor pelos que aguardam assinatura (até 5): cobre webhook perdido e
  // baixa o assinado na hora em que fica pronto. Silencioso: erro aqui não interrompe a aba.
  const autoSynced = React.useRef(false);
  useEffect(() => {
    if (loading || autoSynced.current) return;
    const pending = items.filter((c) => c.signatureStatus === 'sent').slice(0, 5);
    autoSynced.current = true;
    if (pending.length === 0) return;
    (async () => {
      let changed = false;
      for (const c of pending) {
        try {
          const status = await contractSignatureService.sync(c.id);
          if (status !== 'sent') changed = true;
        } catch {
          // segue para o próximo
        }
      }
      if (changed) await load();
    })();
  }, [loading, items, load]);

  const syncOne = async (c: ContractRow) => {
    setBusyId(c.id);
    try {
      const status = await contractSignatureService.sync(c.id);
      showToast(status === 'signed' ? 'Contrato assinado. O PDF assinado foi guardado.' : 'Situação da assinatura atualizada.', 'success');
      await load();
    } catch (e) {
      showToast(signatureErrorMessage(e, 'Não foi possível consultar o provedor de assinatura.'), 'error');
    } finally {
      setBusyId(null);
    }
  };

  const openSigned = async (c: ContractRow) => {
    try {
      await contractSignatureService.open(c.id, false);
    } catch (e) {
      showToast(signatureErrorMessage(e, 'Não foi possível abrir o contrato assinado.'), 'error');
    }
  };

  const pickSignedFile = (c: ContractRow) => {
    setAttachTarget(c);
    attachInput.current?.click();
  };

  const attachSigned = async (file: File | undefined) => {
    const target = attachTarget;
    setAttachTarget(null);
    if (attachInput.current) attachInput.current.value = '';
    if (!file || !target) return;
    setBusyId(target.id);
    try {
      await contractSignatureService.attachSigned(target.id, file);
      showToast('Contrato assinado anexado.', 'success');
      await load();
    } catch (e) {
      showToast(signatureErrorMessage(e, 'Não foi possível anexar o contrato assinado.'), 'error');
    } finally {
      setBusyId(null);
    }
  };

  const signatureLine = (c: ContractRow): string | null => {
    if (!c.signatureStatus) return null;
    const label = SIGNATURE_STATUS_LABELS[c.signatureStatus as ContractSignatureStatus] ?? c.signatureStatus;
    // Assinado já é o chip: a linha diz como e quando, em vez de repetir
    if (c.signatureStatus === 'signed') {
      const when = c.signedAt ? ` em ${formatDateTime(c.signedAt)}` : '';
      return c.signatureProvider === 'manual' ? `Anexado${when}` : `Eletrônica${when}`;
    }
    if (c.signatureStatus === 'sent' && c.signers.length > 0) {
      const done = c.signers.filter((s) => s.status === 'signed').length;
      return `${label} · ${done}/${c.signers.length}`;
    }
    return label;
  };

  const canSend = (c: ContractRow) =>
    canIssue && c.status !== 'cancelled' && !c.hasSignedFile && (c.signatureStatus === null || ['rejected', 'expired', 'cancelled', 'failed'].includes(c.signatureStatus));

  const normalize = (v: string) => v.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
  const filtered = useMemo(() => {
    const term = normalize(search.trim());
    return items.filter((c) => {
      if (statusFilter && c.status !== statusFilter) return false;
      if (from && c.contractDate < from) return false;
      if (to && c.contractDate > to) return false;
      if (term && !normalize(`${c.templateTitle} ${c.issuedByName ?? ''} ${stripHtml(c.content)}`).includes(term)) return false;
      return true;
    });
  }, [items, search, statusFilter, from, to]);

  const hasFilters = search !== '' || statusFilter !== '' || from !== '' || to !== '';
  const clearFilters = () => { setSearch(''); setStatusFilter(''); setFrom(''); setTo(''); setCurrentPage(1); };

  const totalPages = Math.max(1, Math.ceil(filtered.length / itemsPerPage));
  const paged = filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  // Ver = a página do contrato no template da clínica, que já tem o botão de imprimir
  const openContract = (c: ContractRow) => window.open(`?page=contract-print&contractId=${c.id}`, '_blank');

  const confirmCancel = async () => {
    if (!cancelTarget) return;
    setSaving(true);
    try {
      await contractsService.cancel(cancelTarget.id, cancelReason.trim());
      showToast('Contrato cancelado.', 'success');
      setCancelTarget(null);
      setCancelReason('');
      await load();
    } catch (e) {
      showToast(contractIssueErrorMessage(e, 'Não foi possível cancelar o contrato.'), 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="tab-content-section">
      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', mb: '21px' }}>
        <TextField size="small" label="Buscar" placeholder="Modelo, texto ou quem emitiu" value={search} onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }} inputProps={{ maxLength: 100 }} InputLabelProps={{ shrink: true }} sx={searchFieldSx} />
        <TextField select size="small" label="Situação" value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }} InputLabelProps={{ shrink: true }} SelectProps={{ displayEmpty: true }} sx={filterFieldSx}>
          <MenuItem value="">Todas</MenuItem>
          {(Object.keys(CONTRACT_STATUS_LABELS) as ContractStatus[]).map((s) => <MenuItem key={s} value={s}>{CONTRACT_STATUS_LABELS[s]}</MenuItem>)}
        </TextField>
        <TextField size="small" type="date" label="De" value={from} onChange={(e) => { setFrom(e.target.value); setCurrentPage(1); }} InputLabelProps={{ shrink: true }} sx={dateFieldSx} />
        <TextField size="small" type="date" label="Até" value={to} onChange={(e) => { setTo(e.target.value); setCurrentPage(1); }} InputLabelProps={{ shrink: true }} sx={dateFieldSx} />
        <ClearFiltersButton onClick={clearFilters} disabled={!hasFilters} />
        {canIssue && (
          <Tooltip title="Emitir contrato" arrow>
            <span style={{ marginLeft: 'auto' }}>
              <IconButton sx={filterActionButtonSx} aria-label="Emitir contrato" onClick={() => setIssueOpen(true)} disabled={!subjectId}>
                <Add />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Box>

      <Box sx={{ mb: 2, px: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
          <strong>{filtered.length}</strong> {filtered.length === 1 ? 'contrato encontrado' : 'contratos encontrados'}
        </Typography>
      </Box>

      <Box className="c4u-table c4u-table--serial">
        <Box className="c4u-table__header">
          <Box className="admin-plans-header-cell" sx={{ flex: '0 0 100px' }}>Data</Box>
          <Box className="admin-plans-header-cell" sx={{ flex: '1 1 220px', minWidth: 140 }}>Contrato</Box>
          <Box className="admin-plans-header-cell" sx={{ flex: '1 1 200px', minWidth: 140 }}>Vigência</Box>
          <Box className="admin-plans-header-cell c4u-col--md" sx={{ flex: '1 1 160px', minWidth: 120 }}>Emitido por</Box>
          <Box className="admin-plans-header-cell" sx={{ flex: '0 0 150px' }}>Situação</Box>
          <Box className="admin-plans-header-cell" sx={{ flex: '0 0 190px', justifyContent: 'flex-end' }}>Ações</Box>
        </Box>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} sx={{ color: colors.primary }} />
          </Box>
        )}

        {!loading && paged.length === 0 && (
          <Typography sx={{ color: colors.textSecondary, fontSize: '0.875rem', py: 4, textAlign: 'center' }}>
            {items.length === 0
              ? canIssue ? `Nenhum contrato emitido para ${subjectNoun}. Use o botão + para emitir.` : `Nenhum contrato emitido para ${subjectNoun}.`
              : 'Nenhum contrato no filtro.'}
          </Typography>
        )}

        <Box className="admin-plans-table-body">
          {!loading && paged.map((c) => {
            const validity = validityLabel(c);
            const style = STATUS_STYLE[c.status];
            return (
              <React.Fragment key={c.id}>
                <Box className="c4u-table__row" sx={{ opacity: c.status === 'cancelled' ? 0.75 : 1 }}>
                  <Box className="admin-plans-cell" sx={{ display: 'flex', alignItems: 'center', flex: '0 0 100px' }} data-label="Data">
                    <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{formatDate(c.contractDate)}</Typography>
                  </Box>
                  <Box className="admin-plans-cell" sx={{ display: 'flex', alignItems: 'center', flex: '1 1 220px', minWidth: 140 }} data-label="Contrato">
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 600, overflowWrap: 'anywhere' }}>{c.templateTitle}</Typography>
                      <Typography sx={{ fontSize: 12, color: colors.textSecondary }}>
                        v{c.templateVersion}{c.requiresSignature ? ' · exige assinatura' : ''}{c.autoRenew ? ' · renovação automática' : ''}
                      </Typography>
                    </Box>
                  </Box>
                  <Box className="admin-plans-cell" sx={{ display: 'flex', alignItems: 'center', flex: '1 1 200px', minWidth: 140 }} data-label="Vigência">
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontSize: 13 }}>{formatDate(c.validFrom)} a {c.validTo ? formatDate(c.validTo) : 'indeterminado'}</Typography>
                      <Typography sx={{ fontSize: 12, color: validity.color, fontWeight: 600 }}>{validity.text}</Typography>
                    </Box>
                  </Box>
                  <Box className="admin-plans-cell c4u-col--md" sx={{ display: 'flex', alignItems: 'center', flex: '1 1 160px', minWidth: 120 }} data-label="Emitido por">
                    <Typography sx={{ fontSize: 13, overflowWrap: 'anywhere' }}>{c.issuedByName || '—'}</Typography>
                  </Box>
                  <Box className="admin-plans-cell" sx={{ display: 'flex', alignItems: 'center', flex: '0 0 150px' }} data-label="Situação">
                    <Box sx={{ minWidth: 0 }}>
                      <Chip label={CONTRACT_STATUS_LABELS[c.status]} size="small" sx={{ height: 22, fontSize: '0.72rem', fontWeight: 700, backgroundColor: style.bg, color: style.fg }} />
                      {signatureLine(c) && (
                        <Typography sx={{ fontSize: 11.5, mt: 0.5, color: c.signatureStatus === 'signed' ? colors.successHover : c.signatureStatus === 'sent' ? colors.warningSoftText : colors.errorHover }}>
                          {signatureLine(c)}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  <Box className="admin-plans-cell admin-plans-actions" sx={{ flex: '0 0 190px' }} data-label="Ações">
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', alignItems: 'center', width: '100%' }}>
                      <Tooltip title={c.status === 'cancelled' ? `Ver (cancelado por ${c.cancelledByName || '—'} em ${formatDateTime(c.cancelledAt)}: ${c.cancelReason})` : 'Ver e imprimir'} arrow>
                        <IconButton className="c4u-row-action" size="small" onClick={() => openContract(c)} sx={{ backgroundColor: colors.primary, '&:hover': { backgroundColor: colors.primaryHover }, color: colors.white }} aria-label="Ver e imprimir contrato">
                          <Visibility sx={{ fontSize: '1rem' }} />
                        </IconButton>
                      </Tooltip>
                      {c.hasSignedFile && (
                        <Tooltip title="Ver o contrato assinado" arrow>
                          <IconButton className="c4u-row-action" size="small" onClick={() => openSigned(c)} sx={{ backgroundColor: colors.success, '&:hover': { backgroundColor: colors.successHover }, color: colors.white }} aria-label="Ver o contrato assinado">
                            <TaskAlt sx={{ fontSize: '1rem' }} />
                          </IconButton>
                        </Tooltip>
                      )}
                      {c.signatureStatus === 'sent' && (
                        <Tooltip title="Atualizar a situação da assinatura" arrow>
                          <span>
                            <IconButton className="c4u-row-action" size="small" disabled={busyId === c.id} onClick={() => syncOne(c)} sx={{ backgroundColor: '#6c757d', '&:hover': { backgroundColor: '#5a6268' }, color: colors.white, '&.Mui-disabled': { backgroundColor: '#ced4da', color: colors.white } }} aria-label="Atualizar a situação da assinatura">
                              <Sync sx={{ fontSize: '1rem' }} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                      {canSend(c) && (
                        <Tooltip title={c.signatureStatus ? 'Enviar de novo para assinatura' : 'Enviar para assinatura eletrônica'} arrow>
                          <IconButton className="c4u-row-action" size="small" onClick={() => setSignTarget(c)} sx={{ backgroundColor: colors.primaryText, '&:hover': { backgroundColor: colors.primaryHover }, color: colors.white }} aria-label="Enviar para assinatura">
                            <Draw sx={{ fontSize: '1rem' }} />
                          </IconButton>
                        </Tooltip>
                      )}
                      {canSend(c) && (
                        <Tooltip title="Anexar o contrato assinado à mão (PDF)" arrow>
                          <span>
                            <IconButton className="c4u-row-action" size="small" disabled={busyId === c.id} onClick={() => pickSignedFile(c)} sx={{ backgroundColor: '#6c757d', '&:hover': { backgroundColor: '#5a6268' }, color: colors.white, '&.Mui-disabled': { backgroundColor: '#ced4da', color: colors.white } }} aria-label="Anexar o contrato assinado">
                              <UploadFile sx={{ fontSize: '1rem' }} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                      {canCancel && c.status !== 'cancelled' && (
                        <Tooltip title="Cancelar contrato" arrow>
                          <IconButton className="c4u-row-action" size="small" onClick={() => { setCancelReason(''); setCancelTarget(c); }} sx={{ backgroundColor: colors.error, '&:hover': { backgroundColor: colors.errorHover }, color: colors.white }} aria-label="Cancelar contrato">
                            <Block sx={{ fontSize: '1rem' }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </Box>
                </Box>

              </React.Fragment>
            );
          })}
        </Box>
      </Box>

      {filtered.length > 0 && (
        <StandardPagination
          currentPage={currentPage}
          totalPages={totalPages}
          itemsPerPage={itemsPerPage}
          totalItems={filtered.length}
          onPageChange={setCurrentPage}
          onItemsPerPageChange={(n) => { setItemsPerPage(n); setCurrentPage(1); }}
        />
      )}

      <input ref={attachInput} type="file" accept="application/pdf" hidden onChange={(e) => attachSigned(e.target.files?.[0])} />

      <SendForSignatureModal
        open={signTarget !== null}
        contract={signTarget}
        onClose={() => setSignTarget(null)}
        onSent={async () => { setSignTarget(null); showToast('Contrato enviado para assinatura.', 'success'); await load(); }}
      />

      <IssueContractModal
        open={issueOpen}
        subject={subject}
        templates={templates}
        onClose={() => setIssueOpen(false)}
        onIssued={async () => { setIssueOpen(false); showToast('Contrato emitido.', 'success'); await load(); }}
      />

      <ConfirmModal
        isOpen={cancelTarget !== null}
        title="Cancelar contrato"
        message={`Cancelar o contrato "${cancelTarget?.templateTitle ?? ''}" de ${cancelTarget ? formatDate(cancelTarget.contractDate) : ''}? Ele continua na lista, marcado como cancelado, e deixa de contar como contrato vigente.`}
        type="danger"
        confirmButtonText="Cancelar contrato"
        cancelButtonText="Voltar"
        isProcessing={saving}
        reasonLabel="Motivo do cancelamento"
        reasonValue={cancelReason}
        onReasonChange={setCancelReason}
        reasonMinLength={3}
        reasonMaxLength={500}
        onConfirm={confirmCancel}
        onCancel={() => setCancelTarget(null)}
      />
      <Toast message={toast.message} type={toast.type} isVisible={toast.isVisible} onClose={hideToast} />
    </div>
  );
};

interface Props {
  patientId: string;
}

const PatientContractsTab: React.FC<Props> = ({ patientId }) => {
  const subject = useMemo<ContractSubject>(() => ({ kind: 'patient', id: patientId }), [patientId]);
  return <ContractsPanel subject={subject} />;
};

export default PatientContractsTab;
